import { Link } from "react-router-dom";
import { Card, Icon, LoadingRows } from "./components";
import { eventStatusMeta } from "./format";
import { useApi } from "../hooks/useApi";

/**
 * WARTESCHLANGE Punkt 78 — die Karte „Angefragter Tag", neu gebaut nach den Entwürfen 42a und
 * 42c (Antoine, 20.09.2026: „42a und darunter 42c sollen umgesetzt werden, anstatt der
 * bisherigen Kachel. 42b nicht.").
 *
 * ── Was vorher war, und warum es ersetzt wird ────────────────────────────────────────────────
 * Die Karte zeigte je angefragtem Tag eine Zeile: ein Deko-Kalender-Icon, das Datum über zwei
 * Zeilen gebrochen, und daneben gleichrangig „2 Anfragen · 1 Buchung" oder „sonst nichts an
 * diesem Tag" (Punkt 73, 20.09.2026). Der Befund des Entwurfs: das Datum bricht um, das Icon
 * sagt nichts, und die Verfügbarkeit steht als gleichwertiger Text neben dem Datum statt als
 * erste Auskunft.
 *
 * ── Was jetzt steht ─────────────────────────────────────────────────────────────────────────
 * **42a (oben):** links ein Kalenderblatt mit Monatsbalken in der Akzentfarbe, großer Tageszahl
 * und Wochentag. Rechts das lange Datum in EINER Zeile, darunter der Abstand in Tagen und die
 * Kalenderwoche, darunter eine Status-Pille: grün „Frei" oder amber „n weitere Events". Bei
 * einem Konflikt folgt je Parallelveranstaltung eine Zeile mit Statusbalken, Name, Gästezahl,
 * Ort und einem Link zum Öffnen.
 *
 * **42c (darunter):** der Wochenstreifen Montag bis Sonntag. Der angefragte Tag in der
 * Akzentfarbe, andere Tage mit Punkten in der Statusfarbe ihrer Veranstaltungen, darunter eine
 * Legendenzeile („Sa 8.: Hochzeit Weber"), höchstens drei Zeilen, dann „+ n weitere".
 *
 * ── Die vier Zustände des Entwurfs ──────────────────────────────────────────────────────────
 * **frei** · **Konflikt** · **kein Datum erfasst** (das Kalenderblatt zeigt „—", dazu „Datum
 * noch offen" und ein Weg, es festzulegen) · **Vergangenheit** (statt der Frei/Belegt-Pille die
 * neutrale Pille des eigenen Vorgangsstatus — ob an einem vergangenen Abend noch etwas anderes
 * lief, ändert nichts mehr). Dazu der Ladezustand.
 *
 * Der Entwurf zeigt im Zustand „kein Datum" zusätzlich den Freitext aus der Anfrage („Kunde
 * nennt: Ende Mai 2027"). **Den gibt es im System nicht** — ein Datums-Freitext wird nirgends
 * erfasst, und die ganze Anfragennachricht an dieser Stelle auszugeben wäre etwas anderes als
 * das, was der Entwurf meint. Die Kachel nennt deshalb nur, dass das Datum fehlt, und den Weg,
 * es zu setzen. Steht in der Warteschlange als offener Rest.
 *
 * ── Die Regel, die im Entwurf ausdrücklich steht ────────────────────────────────────────────
 * **Niemals Rot.** Eine Parallelveranstaltung ist Information, kein Fehler — sie bekommt Amber,
 * nicht die Fehlerfarbe. Ein Abend, an dem etwas anderes stattfindet, ist kein Problem des
 * Betriebs, sondern eine Tatsache, die er kennen muss.
 *
 * ── Woher die Daten kommen ──────────────────────────────────────────────────────────────────
 * `GET /api/events/:id/angefragte-tage` (`apps/api/src/routes/tagesbelegung.ts`). Die Route
 * liefert seit Punkt 78 zusätzlich `parallel` (Name, Gästezahl, Ort, Status je fremder
 * Veranstaltung), `woche` (Mo–So mit dem, was dort liegt) und `eigenerStatus`. Die beiden Zähler `anfragen` und
 * `buchungen` bleiben unverändert und zählen weiterhin ALLE — auch die, die in `parallel` wegen
 * der Obergrenze nicht mehr aufgeführt sind.
 */

/** Punkt 78: eine fremde Veranstaltung am selben Tag. Spiegel von `routes/tagesbelegung.ts`. */
export interface ParallelVeranstaltung {
  id: string;
  titel: string;
  gaeste: number | null;
  ort: string | null;
  status: string;
}

export interface TagesBelegung {
  datum: string;
  anfragen: number;
  buchungen: number;
  parallel?: ParallelVeranstaltung[];
}

export interface WochenTag {
  datum: string;
  eigenerTag: boolean;
  veranstaltungen?: ParallelVeranstaltung[];
}

/*
 * Die Statusfarben des Entwurfs. Bewusst nur DREI Töne statt einer Farbe je Status: der Streifen
 * soll auf einen Blick sagen „fest / in Verhandlung / neu", nicht die ganze Zustandsmaschine
 * abbilden. Rot kommt nicht vor (siehe Kopfkommentar).
 */
const STATUS_FARBE: Record<string, string> = {
  offer_chosen: "bg-emerald-600",
  paid: "bg-emerald-600",
  payment_failed: "bg-emerald-600",
  completed: "bg-emerald-600",
  offer_sent: "bg-amber-500",
  offer_draft: "bg-amber-500",
  inquiry: "bg-sky-600",
};
const STATUS_FARBE_FALLBACK = "bg-on-surface-variant/50";

/** So viele Zeilen zeigt die Legende unter dem Wochenstreifen — der Entwurf deckelt bei drei. */
const WOCHENLEGENDE_MAX = 3;

export function statusFarbe(status: string): string {
  return STATUS_FARBE[status] ?? STATUS_FARBE_FALLBACK;
}

/**
 * Graues Status-Symbol (Ruhige Hinweise, 13.10.2026). Diese Karte nutzt es selbst nicht mehr
 * (Antoine 17.10.2026: „Genau wie damals, bunt"), der VerschiebenDialog aber weiterhin.
 */
export function StatusSymbol({ status, className = "text-on-surface-variant" }: { status: string; className?: string }) {
  const meta = eventStatusMeta(status);
  return (
    <span title={meta.label} data-status={status} aria-hidden className="inline-flex">
      <Icon name={meta.icon ?? "circle"} className={`!text-[11px] leading-none ${className}`} />
    </span>
  );
}

/** Aus `YYYY-MM-DD` ein Datum in UTC — nie über die Zeitzone, sonst wandert der Tag. */
export function alsDatum(iso: string): Date {
  return new Date(`${iso}T00:00:00.000Z`);
}

/** „Freitag, 7. Mai 2027" — die lange Form aus dem Entwurf. */
export function langesDatum(iso: string): string {
  return alsDatum(iso).toLocaleDateString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * „in 229 Tagen" · „heute" · „vor 12 Tagen".
 *
 * Gerechnet wird auf KALENDERTAGE, nicht auf 24-Stunden-Abstände: „morgen" soll morgen heißen,
 * auch wenn zwischen jetzt und morgen früh nur acht Stunden liegen.
 */
export function abstandInTagen(iso: string, heute: Date): string {
  const ziel = alsDatum(iso).getTime();
  const start = Date.UTC(heute.getFullYear(), heute.getMonth(), heute.getDate());
  const tage = Math.round((ziel - start) / 86_400_000);
  if (tage === 0) return "heute";
  if (tage === 1) return "morgen";
  if (tage === -1) return "gestern";
  return tage > 0 ? `in ${tage} Tagen` : `vor ${Math.abs(tage)} Tagen`;
}

/**
 * Die ISO-Kalenderwoche. Eigene Rechnung statt einer Bibliothek, weil es genau diese eine
 * Stelle betrifft: Donnerstag derselben Woche suchen, dann zählen, die wievielte Woche des
 * Jahres das ist. Das ist die Definition der ISO-Woche.
 */
/**
 * Liegt der Tag hinter uns? Der HEUTIGE Abend zählt ausdrücklich nicht dazu — an ihm findet die
 * Veranstaltung noch statt, und die Frage „ist der Tag frei" ist bis Mitternacht berechtigt.
 *
 * Eigene Funktion, statt `abstandInTagen(...).startsWith("vor ")` zu lesen: eine Anzeigezeile
 * als Entscheidungsgrundlage zu benutzen heißt, dass die nächste Umformulierung des Textes still
 * die Logik ändert.
 */
export function istVergangen(iso: string, heute: Date): boolean {
  const start = Date.UTC(heute.getFullYear(), heute.getMonth(), heute.getDate());
  return alsDatum(iso).getTime() < start;
}

export function isoWoche(iso: string): number {
  const d = alsDatum(iso);
  const tag = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - tag + 3);
  const ersterDonnerstag = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const versatz = (ersterDonnerstag.getUTCDay() + 6) % 7;
  ersterDonnerstag.setUTCDate(ersterDonnerstag.getUTCDate() - versatz + 3);
  return 1 + Math.round((d.getTime() - ersterDonnerstag.getTime()) / (7 * 86_400_000));
}

/**
 * Das leere Kalenderblatt (42a, Zustand „kein Datum erfasst"): Monatsbalken ohne Monat, ein
 * Gedankenstrich statt der Tageszahl. Es hält dieselbe Stelle und dieselbe Größe wie das volle
 * Blatt — die Karte springt dadurch nicht, sobald ein Datum gesetzt wird.
 */
function LeeresKalenderblatt() {
  return (
    <div className="w-[58px] shrink-0 overflow-hidden rounded-[10px] border border-dashed border-outline-variant/60 text-center dark:border-outline/40">
      <div className="bg-surface-container-high px-0 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-on-surface-variant dark:bg-white/[0.08]">
        &nbsp;
      </div>
      <div className="pb-[3px] pt-2 text-[24px] font-bold leading-none text-on-surface-variant/70">—</div>
      <div className="pb-[7px] text-[10.5px] text-on-surface-variant">offen</div>
    </div>
  );
}

/** Das Kalenderblatt aus 42a: Monatsbalken, Tageszahl, Wochentag. */
function Kalenderblatt({ datum }: { datum: string }) {
  const d = alsDatum(datum);
  const monat = d.toLocaleDateString("de-DE", { month: "short", timeZone: "UTC" }).replace(".", "");
  const tagZahl = d.toLocaleDateString("de-DE", { day: "numeric", timeZone: "UTC" });
  const wochentag = d.toLocaleDateString("de-DE", { weekday: "short", timeZone: "UTC" }).replace(".", "");
  return (
    <div className="w-[58px] shrink-0 overflow-hidden rounded-[10px] border border-outline-variant/40 text-center dark:border-outline/30">
      <div className="bg-primary px-0 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-on-primary">{monat}</div>
      <div className="pb-[3px] pt-2 text-[24px] font-bold leading-none tracking-[-0.02em] text-on-background dark:text-inverse-on-surface">
        {tagZahl}
      </div>
      <div className="pb-[7px] text-[10.5px] text-on-surface-variant">{wochentag}</div>
    </div>
  );
}

/**
 * Die Status-Pille. Grün „Frei", amber „n weitere Events".
 *
 * Der Entwurf sagt dazu ausdrücklich: **niemals Rot**. Deshalb steht die Farbwahl hier und nicht
 * beim Aufrufer — wer die Pille benutzt, soll gar nicht erst die Gelegenheit haben, sie rot zu
 * machen.
 */
function StatusPille({ anzahl }: { anzahl: number }) {
  if (anzahl === 0) {
    return (
      <span className="inline-flex items-center gap-[5px] rounded-full bg-emerald-50 px-2 py-[2px] text-[11px] font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">
        <span className="h-[6px] w-[6px] rounded-full bg-emerald-500" />
        Frei
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-[5px] rounded-full bg-amber-50 px-2 py-[2px] text-[11px] font-semibold text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
      <span className="h-[6px] w-[6px] rounded-full bg-amber-500" />
      {anzahl === 1 ? "1 weiteres Event" : `${anzahl} weitere Events`}
    </span>
  );
}

/** Eine Zeile je Parallelveranstaltung (42a, Konfliktzustand). */
function ParallelZeile({ v }: { v: ParallelVeranstaltung }) {
  const zusatz = [v.gaeste != null ? `${v.gaeste} Gäste` : null, v.ort].filter(Boolean).join(" · ");
  return (
    <div
      className="flex items-center gap-[10px] rounded-[9px] bg-surface-container-high/60 px-[10px] py-2 text-[12px] dark:bg-surface-variant/15"
      data-testid={`angefragter-tag-parallel-${v.id}`}
    >
      <span className={`h-[22px] w-[6px] shrink-0 rounded-[3px] ${statusFarbe(v.status)}`} aria-hidden />
      <span className="min-w-0 flex-1 truncate">
        <span className="font-semibold text-on-background dark:text-inverse-on-surface">{v.titel}</span>
        {zusatz && <span className="text-on-surface-variant"> · {zusatz}</span>}
      </span>
      <Link to={`/vorgaenge/${v.id}`} className="shrink-0 text-[12px] font-semibold text-primary hover:underline">
        Öffnen ↗
      </Link>
    </div>
  );
}

/**
 * Die Legendenzeile unter dem Streifen (42c): „Sa 8.: Hochzeit Weber (Zugesagt)".
 *
 * Der Entwurf deckelt sie bei drei Zeilen. Der eigene Tag kommt nicht vor — er steht schon
 * vollständig oben in 42a; ihn hier zu wiederholen, verdrängte nur die Nachbartage, um die es
 * dem Streifen geht.
 */
function Wochenlegende({ woche }: { woche: WochenTag[] }) {
  const zeilen = woche
    .filter((t) => !t.eigenerTag)
    .flatMap((t) => (t.veranstaltungen ?? []).map((v) => ({ tag: t.datum, v })));
  if (zeilen.length === 0) return null;
  const gezeigt = zeilen.slice(0, WOCHENLEGENDE_MAX);
  return (
    <ul className="mt-2 space-y-[3px]" data-testid="angefragter-tag-wochenlegende">
      {gezeigt.map(({ tag, v }) => {
        const d = alsDatum(tag);
        const kurz = d.toLocaleDateString("de-DE", { weekday: "short", timeZone: "UTC" }).replace(".", "");
        const zahl = d.toLocaleDateString("de-DE", { day: "numeric", timeZone: "UTC" });
        return (
          <li key={`${tag}-${v.id}`} className="flex items-center gap-[6px] text-[11.5px] text-on-surface-variant">
            <span className={`h-[5px] w-[5px] shrink-0 rounded-full ${statusFarbe(v.status)}`} aria-hidden />
            <span className="min-w-0 truncate">
              <span className="font-semibold">
                {kurz} {zahl}.
              </span>{" "}
              {v.titel} ({eventStatusMeta(v.status).label})
            </span>
          </li>
        );
      })}
      {zeilen.length > gezeigt.length && (
        <li className="pl-[11px] text-[11.5px] text-on-surface-variant/80">+ {zeilen.length - gezeigt.length} weitere</li>
      )}
    </ul>
  );
}

/** Der Wochenstreifen aus 42c. */
function Wochenstreifen({ woche }: { woche: WochenTag[] }) {
  return (
    <div className="grid grid-cols-7 gap-1" data-testid="angefragter-tag-woche">
      {woche.map((t) => {
        const d = alsDatum(t.datum);
        const kurz = d.toLocaleDateString("de-DE", { weekday: "short", timeZone: "UTC" }).replace(".", "");
        const zahl = d.toLocaleDateString("de-DE", { day: "numeric", timeZone: "UTC" });
        return (
          <Link
            key={t.datum}
            to={`/kalender?tag=${t.datum}`}
            title={`${langesDatum(t.datum)} im Kalender ansehen`}
            className={`rounded-[9px] px-0 pb-[5px] pt-[6px] text-center transition-colors ${
              t.eigenerTag
                ? "bg-primary text-on-primary"
                : "text-on-background hover:bg-surface-container-high dark:text-inverse-on-surface dark:hover:bg-surface-variant/20"
            }`}
          >
            <div className={`text-[10px] font-semibold uppercase tracking-[0.04em] ${t.eigenerTag ? "opacity-85" : "text-on-surface-variant"}`}>
              {kurz}
            </div>
            <div className={`text-[14px] ${t.eigenerTag ? "font-bold" : ""}`}>{zahl}</div>
            {/* Fester 5-px-Platz für die Punkte, damit die Zellen gleich hoch bleiben — auch die
                ohne Veranstaltung. Sonst hüpfte der Streifen je nach Belegung. */}
            <div className="flex h-[5px] items-center justify-center gap-[2px]">
              {(t.veranstaltungen ?? []).slice(0, 3).map((v) => (
                <span
                  key={v.id}
                  className={`h-[5px] w-[5px] rounded-full ${t.eigenerTag ? "bg-on-primary/80" : statusFarbe(v.status)}`}
                />
              ))}
            </div>
          </Link>
        );
      })}
    </div>
  );
}

/**
 * Die neutrale Pille des Zustands „Vergangenheit" (42a).
 *
 * An einem Abend, der vorbei ist, beantwortet „Frei" oder „2 weitere Events" keine Frage mehr.
 * Was zählt, ist, was aus dem eigenen Vorgang geworden ist — deshalb steht hier der Status des
 * Vorgangs selbst, in seinem gewohnten Wortlaut (`EVENT_STATUS`), und in gedeckter Farbe: auch
 * ein abgesagter vergangener Abend ist keine Warnung mehr, sondern Geschichte.
 */
function VergangenPille({ status }: { status: string }) {
  return (
    <span className="inline-flex items-center gap-[5px] rounded-full bg-surface-container-high px-2 py-[2px] text-[11px] font-semibold text-on-surface-variant dark:bg-white/[0.08]">
      <span className="h-[6px] w-[6px] rounded-full bg-on-surface-variant/50" />
      {eventStatusMeta(status).label}
    </span>
  );
}

/**
 * Die Kachel im Zustand „kein Datum erfasst" (42a).
 *
 * Sie ersetzt das, was bis Punkt 78 an dieser Stelle stand: **gar nichts**. Ein Vorgang ohne
 * Datum ist aber keine harmlose Leerstelle — ohne Datum kann niemand prüfen, ob der Abend frei
 * ist, und das Angebot kann nicht zugesagt werden. Die Kachel sagt das und bietet den Weg an.
 */
function KeinDatumKachel({ onDatumFestlegen }: { onDatumFestlegen?: () => void }) {
  return (
    <Card className="space-y-3 p-[16px_18px]" data-testid="builder-angefragte-tage">
      <h3 className="text-beleg-panel text-on-background dark:text-inverse-on-surface">Angefragter Tag</h3>
      <div className="flex items-center gap-[14px]" data-testid="builder-angefragter-tag-offen">
        <LeeresKalenderblatt />
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold text-on-background dark:text-inverse-on-surface">Datum noch offen</div>
          <div className="mt-[2px] text-[12px] text-on-surface-variant">
            Ohne Datum lässt sich nicht prüfen, ob der Tag frei ist.
          </div>
          {onDatumFestlegen && (
            <button
              type="button"
              onClick={onDatumFestlegen}
              className="mt-2 text-[12px] font-semibold text-primary hover:underline"
              data-testid="builder-angefragter-tag-datum-festlegen"
            >
              Datum festlegen
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

export function AngefragteTageKarte({ eventId, onDatumFestlegen }: { eventId: string; onDatumFestlegen?: () => void }) {
  const { data, loading, error } = useApi<{ tage: TagesBelegung[]; woche?: WochenTag[]; eigenerStatus?: string }>(
    eventId ? `/api/events/${eventId}/angefragte-tage` : null,
  );
  const tage = data?.tage ?? [];
  const woche = data?.woche ?? [];

  // Ein Fehler beim Laden wird still uebergangen: die Belegung ist eine Zusatzauskunft, kein
  // Teil des Angebots, und eine rote Meldung an dieser Stelle belastete den Builder mit etwas,
  // das mit dem Angebot nichts zu tun hat.
  if (loading) return <Card className="space-y-2 p-4" data-testid="builder-angefragte-tage"><LoadingRows rows={2} /></Card>;
  if (error || !data) return null;
  // 42a, Zustand „kein Datum erfasst" — seit Punkt 78 sagt die Karte das, statt zu verschwinden.
  if (tage.length === 0) return <KeinDatumKachel onDatumFestlegen={onDatumFestlegen} />;

  const heute = new Date();
  const eigenerStatus = data.eigenerStatus;

  return (
    <Card className="space-y-3 p-[16px_18px]" data-testid="builder-angefragte-tage">
      <h3 className="text-beleg-panel text-on-background dark:text-inverse-on-surface">
        {tage.length === 1 ? "Angefragter Tag" : "Angefragte Tage"}
      </h3>

      {tage.map((tag) => {
        const parallel = tag.parallel ?? [];
        const gesamt = tag.anfragen + tag.buchungen;
        const vergangen = istVergangen(tag.datum, heute);
        return (
          <div key={tag.datum} className="space-y-3" data-testid={`builder-angefragter-tag-${tag.datum}`}>
            {/* 42a — Kalenderblatt links, Textblock rechts. */}
            <div className="flex items-center gap-[14px]">
              <Link to={`/kalender?tag=${tag.datum}`} title="Im Kalender ansehen" className="shrink-0">
                <Kalenderblatt datum={tag.datum} />
              </Link>
              <div className="min-w-0 flex-1">
                {/* Das Datum bricht NICHT um — das war Befund Nummer eins des Entwurfs. Auf
                    schmalem Schirm wird gekürzt, nicht umgebrochen. */}
                <Link
                  to={`/kalender?tag=${tag.datum}`}
                  className="block truncate text-[14px] font-semibold text-on-background hover:underline dark:text-inverse-on-surface"
                  title="Im Kalender ansehen"
                >
                  {langesDatum(tag.datum)}
                </Link>
                <div className="mt-[2px] text-[12px] text-on-surface-variant">
                  {abstandInTagen(tag.datum, heute)} · KW {isoWoche(tag.datum)}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {/* 42a: an einem vergangenen Abend sagt „Frei" nichts mehr — dort steht der
                      Status des eigenen Vorgangs. */}
                  {vergangen && eigenerStatus ? <VergangenPille status={eigenerStatus} /> : <StatusPille anzahl={gesamt} />}
                  <span className="text-[11.5px] text-on-surface-variant/80">
                    {gesamt === 0 ? LEERTEXT : belegungsZusatz(tag)}
                  </span>
                </div>
              </div>
            </div>

            {/* 42a, Konfliktzustand — je Parallelveranstaltung eine Zeile. */}
            {parallel.length > 0 && (
              <div className="space-y-1">
                {parallel.map((v) => (
                  <ParallelZeile key={v.id} v={v} />
                ))}
                {gesamt > parallel.length && (
                  <div className="px-[10px] text-[11.5px] text-on-surface-variant/80">
                    + {gesamt - parallel.length} weitere
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* 42c — der Wochenstreifen, einmal unter allem. */}
      {woche.length > 0 && (
        <div className="border-t border-outline-variant/30 pt-3 dark:border-outline/20">
          <Wochenstreifen woche={woche} />
          <Wochenlegende woche={woche} />
        </div>
      )}
    </Card>
  );
}

/**
 * Der Text für einen Tag, an dem sonst nichts liegt.
 *
 * Antoine hat genau diesen Wortlaut am 20.09.2026 abgenommen (Punkt 73) — vorher stand dort
 * „noch nichts weiter", und dazu sein Befund: „Das ist noch nicht gut formuliert." Der Satz
 * bleibt deshalb unverändert, auch wenn der Entwurf ihn nicht vorsieht: daneben steht jetzt die
 * Pille „Frei", die dasselbe in einem Wort sagt, aber der abgestimmte Satz nennt den Gegenstand
 * („dieser Tag") und grenzt ihn vom eigenen Vorgang ab („sonst"). Beides zusammen ist eindeutig.
 */
const LEERTEXT = "sonst nichts an diesem Tag";

/**
 * Der Nebentext neben der Pille: „2 Anfragen · 1 Buchung".
 *
 * Er sagt, WAS an dem Tag liegt, während die Pille nur sagt, DASS etwas liegt. Beides zusammen
 * ist der Unterschied zwischen „belegt" und „belegt, aber nur mit Anfragen" — und der
 * entscheidet, ob der Betrieb den Termin noch annehmen kann.
 */
function belegungsZusatz(tag: TagesBelegung): string {
  const teile: string[] = [];
  if (tag.anfragen > 0) teile.push(`${tag.anfragen} ${tag.anfragen === 1 ? "Anfrage" : "Anfragen"}`);
  if (tag.buchungen > 0) teile.push(`${tag.buchungen} ${tag.buchungen === 1 ? "Buchung" : "Buchungen"}`);
  return teile.join(" · ");
}
